import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"
import { getReadOnlyMode } from "@/lib/permissions"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// Tombol aksi (tambah / edit / hapus / simpan dll) otomatis disembunyikan saat
// mode read-only (level user Master 2). Satu titik kontrol untuk seluruh aplikasi.
const ACTION_PATTERN = /(tambah|add|edit|hapus|delete|remove|simpan|save|submit|create|update|proses|transfer|import|rekalkulasi|jadikan|urutan|reorder|pindah|revisi|bayar|convert|konversi|kelola|bulk|pembulatan|round|tutup buku|close-period)/i;

// Tombol ikon tanpa teks (mis. pensil / tong sampah / plus) juga ikut disembunyikan.
const ACTION_ICONS = /^(Pencil|PencilLine|SquarePen|Edit|Edit2|Edit3|Trash|Trash2|Plus|PlusCircle|FilePlus|Save)$/;

const hasActionIcon = (node) => {
  if (!node || typeof node !== 'object') return false;
  if (Array.isArray(node)) return node.some(hasActionIcon);
  const name = node.type?.displayName || node.type?.name;
  if (name && ACTION_ICONS.test(name)) return true;
  return hasActionIcon(node.props?.children);
};

const collectText = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(collectText).join(' ');
  if (node.props) return collectText(node.props.children);
  return '';
};

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  if (getReadOnlyMode()) {
    const label = `${props['data-testid'] || ''} ${props['aria-label'] || ''} ${props.title || ''} ${collectText(props.children)}`;
    if (ACTION_PATTERN.test(label) || hasActionIcon(props.children)) return null;
  }
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
